/* eslint-disable camelcase */
import type { ApolloClient } from '@apollo/client';
import { Uppy } from '@uppy/core';
import Dropbox from '@uppy/dropbox';
import GoogleDrivePicker from '@uppy/google-drive-picker';
import OneDrive from '@uppy/onedrive';
import Transloadit from '@uppy/transloadit';
import Url from '@uppy/url';

import type { PluginSourcesType } from 'bundles/asset-admin/constants/AssetUpload';
import type { UppyEventHandlers, UppyEvents, UppyOptionsWithMeta } from 'bundles/asset-admin/types/uppy';
import AssetAttemptPlugin from 'bundles/asset-admin/utils/UppyAssetAttemptPlugin';
import AssetCreationPlugin from 'bundles/asset-admin/utils/UppyAssetCreationPlugin';

export const DEFAULT_KEY = '05912e90e83346abb96c261bf458b615';
export const DEFAULT_TEMPLATE_ID = '9fe2b030809a11e4943d19e60b9af16b';
const UPPY_ID = 'uppyInstance';

// transloadit hosted companion information
const COMPANION = 'https://api2.transloadit.com/companion';
const COMPANION_PATTERN = /\.transloadit\.com$/;

/*
 * Utility function for easily creating an Uppy instance preconfigured to work with our asset service
 * or more generically with configuration parameters
 */
export default function createUppyInstance({
  params = {},
  events = {},
  key = DEFAULT_KEY,
  templateId = DEFAULT_TEMPLATE_ID,
  uppyId = UPPY_ID,
  assetContext,
  pdfTemplate,
  uploadToAssetService = true,
  assetCreationUrl,
  transloaditPluginFields = {},
  pluginSources,
  apolloClient,
}: {
  params: Partial<UppyOptionsWithMeta>;
  events: UppyEvents;
  key: string;
  templateId: string;
  uppyId: string;
  assetContext: { courseId?: string };
  pdfTemplate?: {};
  uploadToAssetService?: boolean;
  assetCreationUrl?: string;
  showPostAssemblyLoader?: boolean;
  transloaditPluginFields?: Record<string, string>;
  pluginSources?: Array<PluginSourcesType> | null;
  apolloClient?: ApolloClient<unknown>;
}) {
  // set `debug: true` for e2e tests, possibly check window.href to contain `teach/e2e-test-000-empty-` ?
  const debug = typeof window === 'undefined' ? false : window.location.href.includes('/e2e-test-');

  const defaultParams = { debug, autoProceed: false };
  // set up cloud to cloud upload companions
  const uppy = new Uppy({ ...defaultParams, ...params, id: uppyId });
  if (pluginSources?.includes('OneDrive')) {
    uppy.use(OneDrive, {
      companionUrl: COMPANION,
      companionAllowedHosts: COMPANION_PATTERN,
    });
  }
  // We've replaced google drive with google picker so that we dont need as many permissions in uppy-integrations oauth integration
  if (pluginSources?.includes('GoogleDrive')) {
    uppy.use(GoogleDrivePicker, {
      companionUrl: COMPANION,
      companionAllowedHosts: COMPANION_PATTERN,
      clientId: '151342174134-32koup6jdi3771teis826ucc7ss9mfno.apps.googleusercontent.com',
      apiKey: 'AIzaSyA6yIeerUkCA44NomofszflXCDOyJVEgeU',
      appId: '151342174134',
    });
  }
  if (pluginSources?.includes('Dropbox')) {
    uppy.use(Dropbox, {
      companionUrl: COMPANION,
      companionAllowedHosts: COMPANION_PATTERN,
    });
  }
  // Transloadit's zoom app has been delisted, causing this to fail.
  // Eventually we could consider hosting our own companion service and using our own zoom app credentials
  // but given low usage of this feature, it's not a priority.
  // https://coursera.slack.com/archives/C01L1LJ8LTY/p1734459261607259
  // .use(Zoom, {
  //   companionUrl: COMPANION,
  //   companionAllowedHosts: COMPANION_PATTERN,
  // })
  if (pluginSources?.includes('Url')) {
    uppy.use(Url, {
      companionUrl: COMPANION,
    });
  }
  // handle asset creation attempts before transloadit uploads
  uppy
    .use(AssetAttemptPlugin, {
      assetContext,
      pdfTemplate,
      enabled: uploadToAssetService,
      assetCreationUrl,
      apolloClient,
    })
    .use(Transloadit, {
      // limit's concurrent uploads, recommended value per the uppy documentation
      limit: 10,
      waitForEncoding: true,
      assemblyOptions: () => {
        return {
          params: {
            notify_url: '',
            wait: true,
            auth: { key },
            template_id: templateId,
          },
          fields: {
            ...transloaditPluginFields,
            attemptId: '',
          },
        };
      },
    })
    // handle actual asset creation after transloadit uploads
    .use(AssetCreationPlugin, { enabled: uploadToAssetService });
  // show a loading status after assembly completes if enabled

  Object.entries(events).forEach(([event, action]) => {
    uppy.on(event as keyof UppyEventHandlers, action as UppyEventHandlers[keyof UppyEventHandlers]);
  });
  return uppy;
}

// Utility function to apply the accessibility patch to remove button/icon of all rendered files.
export const applyRemoveButtonA11yPatch = (containerEl: HTMLElement): void => {
  // Select all file items in the dashboard
  const itemElements = containerEl.querySelectorAll('.uppy-Dashboard-Item');
  itemElements.forEach((itemEl) => {
    const nameEl = itemEl.querySelector('.uppy-Dashboard-Item-name');
    const removeButton = itemEl.querySelector('.uppy-Dashboard-Item-action--remove');

    if (nameEl && removeButton) {
      const fileName = nameEl.getAttribute('title') || nameEl.textContent?.trim();
      if (fileName) {
        // Update the remove button’s aria-label and title with the filename
        const newAriaLabel = `Remove file: ${fileName}`;
        removeButton.setAttribute('aria-label', newAriaLabel);
        removeButton.setAttribute('title', newAriaLabel);
      }
    }
  });
};

// We display Uppy in a modal dialog with h2 title, so we need to update the aria-level attribute to 3.
export const applyTitleAriaLevelPatch = (containerEl: HTMLElement): void => {
  const titleElement = containerEl.querySelector('.uppy-DashboardContent-title');
  if (titleElement instanceof HTMLElement) {
    titleElement.setAttribute('aria-level', '3');
  }
};
